// src/utils/labelPdfHelperStock.js
import jsPDF from "jspdf";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";

// jsPDF me hum built-in fonts use karenge
// Numeric fields ke liye 'courier', baaki ke liye 'helvetica'
const NUMERIC_FIELDS = [
  "grossWt",
  "netWt",
  "stoneWt",
  "diamondWt",
  "qty",
  "pcs",
  "mrp",
];

/**
 * editor px → pdf pt conversion (approx, 96dpi)
 */
const pxToPt = (px) => (px * 72) / 96;

/**
 * Field aliases – agar template me "itemCode" hai
 * aur item object me "StockKeepingUnit", "ItemCode" etc ho,
 * to ye list use hogi.
 */
const FIELD_ALIASES = {
  itemCode: [
    "itemCode",
    "ItemCode",
    "stockKeepingUnit",
    "StockKeepingUnit",
    "sku",
    "SKU",
  ],
  netWt: ["netWt", "NetWt", "netWeight", "NetWeight"],
  grossWt: ["grossWt", "GrossWt", "grossWeight", "GrossWeight"],
  stoneWt: ["stoneWt", "StoneWt"],
  diamondWt: ["diamondWt", "DiamondWt"],
  qty: ["qty", "Qty", "quantity", "Quantity"],
  pcs: ["pcs", "Pcs", "pieces", "Pieces"],
  mrp: ["mrp", "MRP", "rate", "Rate", "amount", "Amount"],
  categoryName: ["categoryName", "CategoryName", "category", "Category"],
  productName: ["productName", "ProductName", "product", "Product"],
  designName: ["designName", "DesignName", "design", "Design"],
  branchName: ["branchName", "BranchName", "branch", "Branch"],
  counterName: ["counterName", "CounterName", "counter", "Counter"],
  vendorName: ["vendorName", "VendorName", "vendor", "Vendor"],
  sampleName: ["sampleName", "SampleName"],
  huidCode: ["huidCode", "HUIDCode", "HuidCode"],
  lotNo: ["lotNo", "LotNo", "lotNumber", "LotNumber"],
};

// ek object se key/case-insensitive se value nikaalne ka helper
const getRawValue = (obj, key) => {
  if (!obj || !key) return undefined;

  // direct
  if (obj[key] !== undefined && obj[key] !== null) return obj[key];

  // case-insensitive
  const lower = key.toLowerCase();
  for (const [k, v] of Object.entries(obj)) {
    if (k.toLowerCase() === lower) {
      return v;
    }
  }

  return undefined;
};

// item se field ka value nikalo (aliases + case-insensitive)
const getFieldValue = (item, fieldKey) => {
  if (!fieldKey || !item) return "";

  // 1) direct / case-insensitive
  let raw = getRawValue(item, fieldKey);

  // 2) aliases try karo
  if (raw === undefined) {
    const aliasList = FIELD_ALIASES[fieldKey] || [];
    for (const alias of aliasList) {
      raw = getRawValue(item, alias);
      if (raw !== undefined && raw !== null) break;
    }
  }

  if (raw === undefined || raw === null) return "";
  return String(raw);
};

/**
 * 🔥 Element ka value resolve
 * Priority:
 *  1) agar contentType === "static" → staticText
 *  2) agar fieldKey se item ka value mila → woh
 *  3) agar item se nahi mila aur prefix hai → prefix (editor preview)
 *  4) last fallback: staticText phir se
 */
const getElementValue = (el, item) => {
  // static text
  if (el.contentType === "static") {
    return el.staticText || "";
  }

  // field-based (ya unknown type → treat as field)
  if (el.fieldKey) {
    const valFromItem = getFieldValue(item, el.fieldKey);
    if (valFromItem) return String(valFromItem);
  }

  // fallback: prefix (editor me jo sample dala tha)
  if (el.prefix) {
    return String(el.prefix);
  }

  // last fallback
  if (el.staticText) {
    return String(el.staticText);
  }

  return "";
};

// TEXT only ke liye helper
const resolveElementText = (el, item) => {
  return getElementValue(el, item);
};

/**
 * 🔹 Ek label ke saare elements jsPDF page pe draw karo
 *  - coordinates already mm me hain (editor se)
 */
const drawLabelOnPdf = async (pdf, layout, item) => {
  const elements = layout.elements || layout.layout?.elements || [];
  if (!elements || !elements.length) return;

  for (const el of elements) {
    const x = el.x ?? 0; // mm
    const y = el.y ?? 0; // mm
    const w = el.w ?? 10; // mm
    const h = el.h ?? 5; // mm

    const type = (el.type || "").toLowerCase();

    // TEXT
    if (type === "text") {
      const txt = resolveElementText(el, item);
      if (!txt) continue;

      const isNumericField =
        el.contentType === "field" &&
        el.fieldKey &&
        NUMERIC_FIELDS.includes(el.fieldKey);

      const align =
        el.textAlign === "center"
          ? "center"
          : el.textAlign === "right"
          ? "right"
          : "left";

      // font select
      const fontName = isNumericField ? "courier" : "helvetica";
      const fontStyle = el.bold ? "bold" : "normal";
      pdf.setFont(fontName, fontStyle);

      // font size: editor px → pdf pt
      const pxSize = el.fontSize || 8;
      const ptSize = pxToPt(pxSize);
      pdf.setFontSize(ptSize);

      // text ko black rakho (safety)
      pdf.setTextColor(0, 0, 0);

      // Text box ke andar vertical positioning (thoda neechay)
      const textY = y + h * 0.7;
      let textX = x + 0.2; // left me thoda margin

      if (align === "center") {
        textX = x + w / 2;
      } else if (align === "right") {
        textX = x + w - 0.2;
      }

      pdf.text(txt, textX, textY, {
        align,
        maxWidth: w - 0.4,
      });
    }

    // BARCODE
    else if (type === "barcode") {
      const val = getElementValue(el, item);
      if (!val) continue;

      // JsBarcode ke liye temporary canvas
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // thoda high-res – width/height px approx
      canvas.width = 600;
      canvas.height = 200;

      try {
        JsBarcode(canvas, val, {
          format: el.barcodeFormat || "CODE128",
          displayValue: false,
          margin: 0,
        });

        const dataUrl = canvas.toDataURL("image/png");
        pdf.addImage(dataUrl, "PNG", x, y, w, h);
      } catch (err) {
        console.error("Barcode render error:", err);
      }
    }

    // QR CODE
    else if (type === "qrcode") {
      const val = getElementValue(el, item);
      if (!val) continue;

      try {
        const sizePx = 512; // high-res QR
        const dataUrl = await QRCode.toDataURL(val, {
          width: sizePx,
          margin: 0,
          errorCorrectionLevel: "M",
          color: { dark: "#000000", light: "#FFFFFF" },
        });

        pdf.addImage(dataUrl, "PNG", x, y, w, h);
      } catch (err) {
        console.error("QR render error:", err);
      }
    }
  }
};

/**
 * 🔹 PUBLIC: jitne items, utne labels → ek PDF
 *  - html2canvas ka use nahi, direct jsPDF drawing
 */
export const generateLabelsPdfForStock = async (items, template) => {
  if (!Array.isArray(items) || !items.length || !template) return;

  const layout = template.layout || template;

  const widthMm = layout?.widthMm || 50;
  const heightMm = layout?.heightMm || 25;

  const pdf = new jsPDF({
    orientation: widthMm > heightMm ? "landscape" : "portrait",
    unit: "mm",
    format: [widthMm, heightMm],
  });

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (i > 0) {
      pdf.addPage([widthMm, heightMm]);
    }

    await drawLabelOnPdf(pdf, layout, item);
  }

  const pdfBlob = pdf.output("blob");
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, "_blank");
};

export default {
  generateLabelsPdfForStock,
};
